import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, User, LogOut, Heart, ShieldCheck, Home, Image, Video, Music, Maximize, Mail } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { PricingModal } from "@/components/PricingModal";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { CreditsDisplay } from "@/components/CreditsDisplay";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export function Navigation() {
  const { t } = useTranslation();
  const location = useLocation();
  const [isPricingOpen, setIsPricingOpen] = useState(false);
  const { isAdmin } = useUserRole();

  // Safely get auth context with fallback
  let user = null;
  let signOut = async () => {};

  try {
    const auth = useAuth();
    user = auth.user;
    signOut = auth.signOut;
  } catch (error) {
    // Auth context not yet available
  }

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      // Sign out error handled by auth context
    }
  };

  const userInitials = user?.email?.[0].toUpperCase() || "U";

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50 backdrop-blur-md">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          <img src="/logo.png" alt="Lovers Labs Logo" width={48} height={48} loading="lazy" className="w-12 h-12" />
          <span
            className="text-xl font-bold bg-clip-text text-transparent"
            style={{
              backgroundImage: "linear-gradient(135deg, hsl(var(--gold)), hsl(var(--scarlet-red)))",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
            }}
          ></span>
        </Link>

        {/* User Actions */}
        <div className="flex items-center gap-4">
          {isAdmin && <LanguageSwitcher />}
          {user ? (
            <>
              <CreditsDisplay onClick={() => setIsPricingOpen(true)} />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                    <Avatar className="h-10 w-10 border-2" style={{ borderColor: "hsl(var(--gold))" }}>
                      <AvatarImage src={user.user_metadata?.avatar_url} alt={user.email || ""} />
                      <AvatarFallback style={{ background: "var(--gradient-premium)", color: "white" }}>
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 glass-premium border-premium" align="end">
                  <div className="flex items-center justify-start gap-2 p-2">
                    <div className="flex flex-col space-y-1 leading-none">
                      <p className="font-medium text-sm">{user.email}</p>
                      <p className="text-xs text-foreground/60">{t("nav.authenticatedAccount")}</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs text-muted-foreground">{t("nav.navigation")}</DropdownMenuLabel>
                  <DropdownMenuItem asChild>
                    <Link to="/" className={`cursor-pointer ${location.pathname === "/" ? "bg-accent" : ""}`}>
                      <Home className="mr-2 h-4 w-4" />
                      {t("nav.home")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      to="/generator"
                      className={`cursor-pointer ${location.pathname === "/generator" ? "bg-accent" : ""}`}
                    >
                      <Sparkles className="mr-2 h-4 w-4" />
                      {t("nav.imageGenerator")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      to="/video-generator"
                      className={`cursor-pointer ${location.pathname === "/video-generator" ? "bg-accent" : ""}`}
                    >
                      <Video className="mr-2 h-4 w-4" />
                      {t("nav.videoGenerator")}
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link
                        to="/audio-generator"
                        className={`cursor-pointer ${location.pathname === "/audio-generator" ? "bg-accent" : ""}`}
                      >
                        <Music className="mr-2 h-4 w-4" />
                        {t("nav.audioGenerator")}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem asChild>
                    <Link
                      to="/video-upscale"
                      className={`cursor-pointer ${location.pathname === "/video-upscale" ? "bg-accent" : ""}`}
                    >
                      <Maximize className="mr-2 h-4 w-4" />
                      {t("nav.videoUpscale")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs text-muted-foreground">{t("nav.account")}</DropdownMenuLabel>
                  <DropdownMenuItem asChild>
                    <Link
                      to="/dashboard"
                      className={`cursor-pointer ${location.pathname === "/dashboard" ? "bg-accent" : ""}`}
                    >
                      <User className="mr-2 h-4 w-4" />
                      {t("nav.dashboard")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setIsPricingOpen(true)} className="cursor-pointer">
                    <Heart className="mr-2 h-4 w-4 fill-[#FFA51C] text-primary" />
                    {t("nav.loversPlus")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    {t("nav.support")}
                  </DropdownMenuLabel>
                  <DropdownMenuItem asChild>
                    <a href="mailto:support@loverslabs.art" className="cursor-pointer">
                      <Mail className="mr-2 h-4 w-4" />
                      {t("nav.contactSupport")}
                    </a>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs text-muted-foreground">
                        {t("nav.administration")}
                      </DropdownMenuLabel>
                      <DropdownMenuItem asChild>
                        <Link
                          to="/admin"
                          className={`cursor-pointer ${location.pathname === "/admin" ? "bg-accent" : ""}`}
                        >
                          <ShieldCheck className="mr-2 h-4 w-4" />
                          {t("nav.adminPanel")}
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer">
                    <LogOut className="mr-2 h-4 w-4" />
                    {t("nav.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/auth">
                <Button variant="outline" className="border-premium">
                  {t("nav.login")}
                </Button>
              </Link>
              <Link to="/edit">
                <Button variant="gradient" className="flex items-center">
                  <Sparkles className="w-4 h-4 mr-2" />
                  {t("nav.createNow")}
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>

      <PricingModal isOpen={isPricingOpen} onClose={() => setIsPricingOpen(false)} />
    </nav>
  );
}
